/**
 * The workspace document's `pages` tree (spec §7.5, §8.25): pages and, M24, folders. Pure reads and
 * the writes the page tree, its menus and the page-link block need, all on the workspace doc.
 */
import { LoroText, type LoroDoc, type LoroTree, type LoroTreeNode, type TreeID } from "loro-crdt";

export type PageRowKind = "page" | "folder";

export interface PageRow {
  treeId: TreeID;
  kind: PageRowKind;
  /** a page's document; absent on a folder */
  docId?: string;
  /** a folder's own title; a page's title lives in the registry */
  title: string;
  depth: number;
  parent: TreeID | null;
  index: number;
  /** a folder's number of direct children */
  children: number;
}

export function pagesTree(doc: LoroDoc): LoroTree {
  const tree = doc.getTree("pages");
  tree.enableFractionalIndex(0);
  return tree;
}

const kindOf = (n: LoroTreeNode): PageRowKind => (n.data.get("kind") === "folder" ? "folder" : "page");

function titleOf(n: LoroTreeNode): string {
  const t = n.data.get("title");
  return t instanceof LoroText ? t.toString() : typeof t === "string" ? t : "";
}

/** Every node in tree order, folders and pages; `collapsed` folders hide their descendants. */
export function readPageRows(doc: LoroDoc, collapsed: ReadonlySet<string> = new Set()): PageRow[] {
  const out: PageRow[] = [];
  const walk = (nodes: LoroTreeNode[] | undefined, depth: number, parent: TreeID | null) => {
    nodes?.forEach((n, index) => {
      const kind = kindOf(n);
      const kids = n.children() ?? [];
      out.push({
        treeId: n.id,
        kind,
        docId: kind === "page" ? String(n.data.get("docId") ?? "") : undefined,
        title: kind === "folder" ? titleOf(n) : "",
        depth,
        parent,
        index,
        children: kids.length,
      });
      if (kind === "folder" && !collapsed.has(n.id)) walk(kids, depth + 1, n.id);
    });
  };
  walk(doc.getTree("pages").roots(), 0, null);
  return out;
}

export const samePageRows = (a: PageRow[], b: PageRow[]) => JSON.stringify(a) === JSON.stringify(b);

/** The titles of the folders above a page, outermost first (empty at the root or when not found). */
export function folderPath(doc: LoroDoc, docId: string): string[] {
  const tree = doc.getTree("pages");
  const find = (nodes: LoroTreeNode[] | undefined, path: string[]): string[] | null => {
    for (const n of nodes ?? []) {
      if (kindOf(n) === "page" && n.data.get("docId") === docId) return path;
      if (kindOf(n) === "folder") {
        const hit = find(n.children(), [...path, titleOf(n)]);
        if (hit) return hit;
      }
    }
    return null;
  };
  return find(tree.roots(), []) ?? [];
}

/** The folder path of every page, by docId: for a selector that must not depend on which page it is
 *  asked about (`useLoroSelector` caches per document version, not per argument). */
export function folderPaths(doc: LoroDoc): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const walk = (nodes: LoroTreeNode[] | undefined, path: string[]) => {
    for (const n of nodes ?? []) {
      if (kindOf(n) === "page") {
        const id = n.data.get("docId");
        if (typeof id === "string" && !(id in out)) out[id] = path;
      } else if (kindOf(n) === "folder") walk(n.children(), [...path, titleOf(n)]);
    }
  };
  walk(doc.getTree("pages").roots(), []);
  return out;
}

/** Whether `target` is `node` itself or lies beneath it (a folder cannot be dropped into itself). */
export function isSelfOrDescendant(doc: LoroDoc, node: TreeID, target: TreeID): boolean {
  let cur: LoroTreeNode | undefined = doc.getTree("pages").getNodeByID(target);
  while (cur) {
    if (cur.id === node) return true;
    cur = cur.parent();
  }
  return false;
}

export function createFolder(doc: LoroDoc, title: string, parent?: TreeID, index?: number): TreeID {
  const node = pagesTree(doc).createNode(parent, index);
  node.data.set("kind", "folder");
  const t = node.data.setContainer("title", new LoroText());
  if (title) t.insert(0, title);
  doc.commit();
  return node.id;
}

export function createPageNode(doc: LoroDoc, docId: string, parent?: TreeID): TreeID {
  const node = pagesTree(doc).createNode(parent);
  node.data.set("docId", docId);
  doc.commit();
  return node.id;
}

/** Move a page or folder under `parent` (undefined = the root) at `index`. Refuses a cycle. */
export function movePageNode(doc: LoroDoc, node: TreeID, parent: TreeID | undefined, index: number): boolean {
  if (parent && isSelfOrDescendant(doc, node, parent)) return false;
  pagesTree(doc).move(node, parent, index);
  doc.commit();
  return true;
}

export function movePageNodeRelative(doc: LoroDoc, node: TreeID, target: TreeID, where: "before" | "after"): boolean {
  if (node === target) return false;
  const tree = pagesTree(doc);
  if (isSelfOrDescendant(doc, node, target)) return false;
  const n = tree.getNodeByID(node);
  const ref = tree.getNodeByID(target);
  if (!n || !ref) return false;
  if (where === "before") n.moveBefore(ref);
  else n.moveAfter(ref);
  doc.commit();
  return true;
}

/** Dissolve a folder (Q71): its children take its place, in order, and the folder goes. */
export function dissolveFolder(doc: LoroDoc, folder: TreeID): void {
  const tree = pagesTree(doc);
  const node = tree.getNodeByID(folder);
  if (!node || kindOf(node) !== "folder") return;
  const kids = node.children() ?? [];
  let anchor: LoroTreeNode = node;
  for (const k of kids) {
    k.moveAfter(anchor);
    anchor = k;
  }
  tree.delete(folder);
  doc.commit();
}

/** Delete an empty folder; a folder with contents is dissolved instead, never emptied (Q71). */
export function deleteEmptyFolder(doc: LoroDoc, folder: TreeID): boolean {
  const node = pagesTree(doc).getNodeByID(folder);
  if (!node || kindOf(node) !== "folder" || (node.children()?.length ?? 0) > 0) return false;
  pagesTree(doc).delete(folder);
  doc.commit();
  return true;
}
